import {
  useMutation,
  useQueryClient,
  UseMutationResult,
} from "@tanstack/react-query"
import { AxiosResponse } from "axios"

import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import { queryKeys } from "api/queryKeys"
import { runAutomationScript, cancelJob } from "api/Repos/automation"
import {
  RunAutomationScriptResponse,
  ScriptExecutionParameters,
  OutputProject,
} from "types/automation.types"

/**
 * React-query mutations for the Automation domain. Notification-free (call
 * sites own their messaging); both refresh the jobs list via invalidation so
 * new/cancelled jobs show up on the next poll tick at the latest.
 */

interface RunScriptArgs {
  scriptName: string
  parameters: ScriptExecutionParameters
  outputProject?: OutputProject
}

export const useRunScriptMutation = (): UseMutationResult<
  AxiosResponse<RunAutomationScriptResponse>,
  Error,
  RunScriptArgs
> => {
  const getAxios = useAuthedAxios()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ scriptName, parameters, outputProject }) =>
      runAutomationScript(
        await getAxios(),
        scriptName,
        parameters,
        outputProject
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.automation.jobs }),
  })
}

export const useCancelJobMutation = (): UseMutationResult<
  AxiosResponse<unknown>,
  Error,
  string
> => {
  const getAxios = useAuthedAxios()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (jobId: string) => cancelJob(await getAxios(), jobId),
    onSuccess: (_res, jobId) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.automation.jobStatus(jobId),
      })
      return queryClient.invalidateQueries({
        queryKey: queryKeys.automation.jobs,
      })
    },
  })
}
